/* MASONTEX · Universal Sample Payment · canonical checkout controller */
(() => {
  'use strict';

  const STORAGE_KEY = 'masontexSampleRequestV2';
  const PAID_SAMPLE_QUANTITY = 1;
  const clean = (value = '') => String(value || '').trim();
  const readSaved = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  };
  const writeSaved = (payload) => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (_) {}
  };

  const init = (root) => {
    if (!root || root.dataset.msUniversalPaymentReady === '1') return;
    root.dataset.msUniversalPaymentReady = '1';

    let mapping = { products: [] };
    try {
      mapping = JSON.parse(root.querySelector('[data-ms-sample-map]')?.textContent || '{"products":[]}');
    } catch (error) {
      console.error('[MASONTEX Universal Sample] Invalid sample mapping payload.', error);
    }
    const products = Array.isArray(mapping?.products) ? mapping.products : [];

    let i18n = {};
    try { i18n = JSON.parse(root.querySelector('[data-ms-pay-i18n]')?.textContent || '{}'); }
    catch (error) {
      console.error('[MASONTEX Universal Sample] Invalid i18n payload.', error);
    }

    const params = new URLSearchParams(window.location.search);
    const saved = readSaved();
    const explicitSource = clean(params.get('source')).toLowerCase();
    const savedSource = clean(saved.source).toLowerCase();
    const source = explicitSource || (params.has('product') ? 'product' : (savedSource || 'product'));
    const fromForm = source === 'form';
    const context = fromForm ? saved : {};

    const selectedHandle = clean(params.get('product') || context.handle);
    const requestedCheckoutProduct = clean(params.get('checkout_product') || context.checkoutProduct);
    const selected = products.find((item) => clean(item?.handle) === selectedHandle) || null;
    const checkoutProductMatches = Boolean(selected) && (!requestedCheckoutProduct || clean(selected.paymentHandle) === requestedCheckoutProduct);
    const variants = selected && Array.isArray(selected.variants)
      ? selected.variants.filter((variant) => variant && variant.available === true && Number(variant.price) > 0 && clean(variant.id))
      : [];
    const checkoutMapped = Boolean(selected && checkoutProductMatches && variants.length);

    const defaults = {
      model: selected?.model || selected?.title || i18n.selectedHelmet || 'SELECTED HELMET',
      market: root.dataset.previewMarket || 'International / Address required',
      reference: root.dataset.previewReference || 'SAMPLE ORDER'
    };
    const values = {
      model: selected
        ? clean(selected.model || selected.title || defaults.model)
        : clean(params.get('model') || context.model || defaults.model),
      quantity: PAID_SAMPLE_QUANTITY,
      market: clean(params.get('market') || params.get('country') || context.market || context.country || defaults.market),
      reference: clean(params.get('reference') || params.get('ref') || context.reference || defaults.reference)
    };

    const currency = root.dataset.currency || 'USD';
    const airShippingCents = Number.parseInt(root.dataset.airShipping || '0', 10) || 0;
    const primaryLabel = clean(root.dataset.primaryLabel) || i18n.primaryLabel || 'PROCEED TO SECURE CHECKOUT';
    let selectedShipping = 'air';
    let selectedVariantButton = null;

    const money = (cents) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
          currencyDisplay: 'code',
          minimumFractionDigits: 2
        }).format(Number(cents || 0) / 100).replace(/\u00a0/g, ' ');
      } catch (_) {
        return `${currency} ${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    };
    const setText = (role, value) => root.querySelectorAll(`[data-pay-field="${role}"]`).forEach((node) => { node.textContent = value; });
    const setAmount = (role, value) => root.querySelectorAll(`[data-pay-amount="${role}"]`).forEach((node) => { node.textContent = value; });

    const titleNodes = root.querySelectorAll('[data-pay-product-title]');
    const imageNodes = root.querySelectorAll('[data-pay-product-image]');
    if (selected) {
      titleNodes.forEach((node) => { node.textContent = selected.title || selected.model || values.model; });
      imageNodes.forEach((node) => {
        if (selected.image) {
          node.src = selected.image;
          node.alt = selected.title || selected.model || values.model;
          node.hidden = false;
          node.previousElementSibling?.setAttribute?.('hidden', '');
        }
      });
    }

    const buildRequestUrl = () => {
      const requestParams = new URLSearchParams({ intent: clean(params.get('intent') || context.intent || 'sample'), source: 'payment-edit', restore: '1' });
      if (selectedHandle) requestParams.set('product', selectedHandle);
      if (values.model) requestParams.set('model', values.model);
      return `/pages/order-a-sample?${requestParams.toString()}`;
    };
    const buildProductUrl = () => selected?.url || (selectedHandle ? `/products/${encodeURIComponent(selectedHandle)}` : '/pages/order-a-sample?intent=sample');
    const syncContextLinks = () => {
      const edit = root.querySelector('[data-pay-edit]');
      const back = root.querySelector('[data-pay-back]');
      if (fromForm) {
        const requestUrl = buildRequestUrl();
        if (edit) { edit.href = requestUrl; edit.innerHTML = `${i18n.editRequest || 'EDIT REQUEST'} <span aria-hidden="true">↗</span>`; }
        if (back) { back.href = requestUrl; back.innerHTML = `<span>←</span> ${i18n.backToRequest || 'BACK TO REQUEST'}`; }
      } else {
        const productUrl = buildProductUrl();
        if (edit) { edit.href = productUrl; edit.innerHTML = `${i18n.backToProduct || 'BACK TO PRODUCT'} <span aria-hidden="true">↗</span>`; }
        if (back) { back.href = productUrl; back.innerHTML = `<span>←</span> ${i18n.backToProduct || 'BACK TO PRODUCT'}`; }
      }
    };

    setText('model', values.model);
    setText('quantity', values.quantity);
    setText('summary-quantity', values.quantity);
    setText('market', values.market);
    setText('reference', values.reference);
    syncContextLinks();

    const sizeOptions = root.querySelector('[data-pay-size-options]');
    const shippingButtons = [...root.querySelectorAll('[data-pay-shipping]')];
    const checkoutButton = root.querySelector('[data-pay-checkout]');

    const buildSizeButtons = () => {
      if (!sizeOptions) return [];
      sizeOptions.innerHTML = '';
      if (!checkoutMapped) {
        const note = document.createElement('p');
        note.className = 'ms-pay__choice-error';
        note.dataset.msSampleUnmappedNote = '';
        note.innerHTML = `<strong>${i18n.notAvailable || 'SAMPLE CHECKOUT NOT AVAILABLE'}</strong><br>${i18n.notAvailableNote || 'This helmet does not currently have an approved payable sample SKU. Return to the request page to submit a sample inquiry.'}`;
        sizeOptions.append(note);
        return [];
      }

      return variants.map((variant) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ms-pay__size-option';
        button.dataset.paySize = '';
        button.dataset.variantId = clean(variant.id);
        button.dataset.variantTitle = clean(variant.title);
        button.dataset.unitPrice = String(Number(variant.price) || 0);
        button.dataset.available = 'true';
        button.setAttribute('aria-pressed', 'false');

        const title = document.createElement('strong');
        title.textContent = clean(variant.title) || 'SAMPLE';
        const price = document.createElement('small');
        price.textContent = clean(variant.priceText) || money(variant.price);
        button.append(title, price);
        sizeOptions.append(button);
        return button;
      });
    };

    const sizeButtons = buildSizeButtons();
    const getSelectedUnit = () => Number.parseInt(selectedVariantButton?.dataset.unitPrice || '0', 10) || 0;
    const getSelectedSize = () => clean(selectedVariantButton?.dataset.variantTitle);
    const getSelectedVariantId = () => clean(selectedVariantButton?.dataset.variantId);
    const getShippingCents = () => selectedShipping === 'forwarder' ? 0 : airShippingCents;

    const updateShippingNote = () => {
      const shippingNote = root.querySelector('[data-pay-shipping-note]');
      if (!shippingNote) return;
      shippingNote.textContent = selectedShipping === 'forwarder'
        ? (i18n.forwarderNote || 'Enter your designated freight forwarder’s China address at Shopify Checkout to receive the USD $0 China-forwarder delivery rate.')
        : (i18n.shippingNoteIntl || `International sample shipping is fixed at ${money(airShippingCents)} per order. No remote-area surcharge is added by MASONTEX. Duties and import taxes, if applicable, are separate.`);
    };

    const updateTotals = () => {
      const shipping = getShippingCents();
      setAmount('shipping', money(shipping));
      setText('shipping-method', selectedShipping === 'forwarder' ? (i18n.chinaForwarder || 'CHINA FREIGHT FORWARDER') : (i18n.intlShipping || 'INTERNATIONAL SAMPLE SHIPPING'));
      updateShippingNote();

      if (!checkoutMapped) {
        root.classList.add('is-unmapped');
        setAmount('unit', i18n.toConfirm || 'TO CONFIRM');
        setAmount('subtotal', i18n.toConfirm || 'TO CONFIRM');
        setAmount('total', i18n.toConfirm || 'TO CONFIRM');
        setText('size', i18n.toConfirm || 'TO CONFIRM');
        if (checkoutButton) {
          checkoutButton.disabled = true;
          checkoutButton.classList.add('is-disabled');
          checkoutButton.setAttribute('aria-disabled', 'true');
          const label = checkoutButton.querySelector('span');
          if (label) label.textContent = root.dataset.pendingLabel || i18n.notAvailable || 'SAMPLE CHECKOUT NOT AVAILABLE';
        }
        return;
      }

      root.classList.remove('is-unmapped');
      const unit = getSelectedUnit();
      const subtotal = unit * PAID_SAMPLE_QUANTITY;
      const total = subtotal + shipping;
      setAmount('unit', money(unit));
      setAmount('subtotal', money(subtotal));
      setAmount('total', money(total));
      setText('size', getSelectedSize() || i18n.selectSize || 'SELECT SIZE');
      if (checkoutButton) {
        const ready = Boolean(getSelectedVariantId()) && unit > 0;
        checkoutButton.disabled = !ready;
        checkoutButton.classList.toggle('is-disabled', !ready);
        checkoutButton.setAttribute('aria-disabled', String(!ready));
        const label = checkoutButton.querySelector('span');
        if (label && ready && !checkoutButton.classList.contains('is-loading')) label.textContent = primaryLabel;
      }
    };

    const chooseSize = (choice) => {
      if (!checkoutMapped || !choice) return;
      selectedVariantButton = choice;
      sizeButtons.forEach((item) => {
        const active = item === choice;
        item.classList.toggle('is-selected', active);
        item.setAttribute('aria-pressed', String(active));
      });
      updateTotals();
    };

    const chooseShipping = (choice) => {
      if (!choice) return;
      selectedShipping = choice.dataset.payShipping === 'forwarder' ? 'forwarder' : 'air';
      shippingButtons.forEach((item) => {
        const active = item === choice;
        item.classList.toggle('is-selected', active);
        item.setAttribute('aria-pressed', String(active));
      });
      updateTotals();
    };

    sizeButtons.forEach((choice) => choice.addEventListener('click', () => chooseSize(choice)));
    shippingButtons.forEach((choice) => choice.addEventListener('click', () => chooseShipping(choice)));

    const requestedSize = clean(params.get('size') || context.size).toUpperCase();
    if (checkoutMapped) {
      chooseSize((requestedSize ? sizeButtons.find((item) => clean(item.dataset.variantTitle).toUpperCase() === requestedSize) : null) || sizeButtons[0]);
    }
    const requestedShipping = clean(params.get('shipping') || context.shipping).toLowerCase();
    chooseShipping((requestedShipping === 'forwarder' ? shippingButtons.find((item) => item.dataset.payShipping === 'forwarder') : shippingButtons.find((item) => item.dataset.payShipping === 'air')) || shippingButtons[0]);
    updateTotals();

    if (!checkoutButton || !checkoutMapped) return;
    checkoutButton.addEventListener('click', async () => {
      if (checkoutButton.classList.contains('is-loading')) return;
      const variantId = Number(getSelectedVariantId());
      const unit = getSelectedUnit();
      if (!variantId || unit <= 0) {
        window.alert(i18n.selectSizeAlert || 'Please select an available sample size.');
        return;
      }

      checkoutButton.classList.add('is-loading');
      checkoutButton.disabled = true;
      const label = checkoutButton.querySelector('span');
      const originalLabel = label?.textContent || primaryLabel;
      if (label) label.textContent = i18n.preparingCheckout || 'PREPARING SECURE CHECKOUT…';

      const shippingPreference = selectedShipping === 'forwarder'
        ? 'China freight forwarder — enter China forwarder address at checkout'
        : `International sample shipping — fixed ${money(airShippingCents)} per order`;

      try {
        const clearResponse = await fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
        });
        if (!clearResponse.ok) throw new Error('Unable to clear the existing cart safely');

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: PAID_SAMPLE_QUANTITY,
              properties: {
                'Sample model': values.model,
                'Sample size': getSelectedSize(),
                'Sample order reference': values.reference,
                'Market': values.market,
                'Shipping preference': shippingPreference,
                'Sample pricing note': 'Evaluation sample pricing only; one paid sample per checkout; final wholesale pricing is quoted separately.'
              }
            }]
          })
        });
        if (!response.ok) throw new Error((await response.text().catch(() => '')) || 'Unable to prepare checkout');

        writeSaved({
          ...(fromForm ? saved : {}),
          source: fromForm ? 'form' : 'product',
          intent: clean(params.get('intent') || context.intent || 'sample'),
          handle: selected.handle,
          model: values.model,
          quantity: PAID_SAMPLE_QUANTITY,
          market: values.market,
          reference: values.reference,
          checkoutProduct: selected.paymentHandle,
          size: getSelectedSize(),
          shipping: selectedShipping,
          savedAt: Date.now()
        });
        window.location.assign('/checkout');
      } catch (error) {
        console.error('[MASONTEX Universal Sample]', error);
        checkoutButton.disabled = false;
        checkoutButton.classList.remove('is-loading');
        if (label) label.textContent = originalLabel;
        updateTotals();
        window.alert(i18n.prepareError || 'Secure checkout could not be prepared. Please refresh the page or contact MASONTEX support.');
      }
    });
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-ms-universal-payment-root]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
